import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as PartsNotFoundImage } from 'assets/images/parts-not-found.svg';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';
import { useTranslation } from 'react-i18next';
import useHttp from 'utils/hooks/useHttp';
import { useLocation, useSearchParams } from 'react-router-dom';
import { IBatteryCatalogFilter, IBodyResponseBattery } from 'interfaces/battery';
import CatalogNavbar from 'components/navbar/catalog-navbar/CatalogNavbar';
import './CatalogBatteries.scss';
import ProductsViewMode from 'components/ProductsViewMode/ProductsViewMode';
import { DEFAULT_VIEW } from 'components/ProductsViewMode/ProductsViewMode.constants';
import ProductsViewModeChanger from 'components/ProductsViewModeChanger/ProductsViewModeChanger';
import { CATALOG_BATTERY } from 'components/ProductsViewModeChanger/products-view-mode-changer-utils';
import List from 'components/List/List';
import { ICategory } from 'views/Sidebar/Sidebar.model';
import CatalogBatteryFilter from './CatalogBatteryFilter/CatalogBatteryFilter';
import axios from 'axios';
import { sortByOptions } from 'components/SortBy/SortBy.map';
import SortBy, { SortByParams } from 'components/SortBy/SortBy';
import { filterObject } from '../../../../utils/utils';
import TextInput from '../../../../components/inputs/TextInput/TextInput';
import { isRetailerMecadepotChild } from 'utils/isRetailerMecadepotChild';
import { ICustomValidation } from 'utils/hooks/http/search-by-reference.http';

const CatalogBatteries: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const http = useHttp();
  const { search } = useLocation();

  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [refData, setRefData] = useState<IBodyResponseBattery>();
  const [filterData, setFilterData] = useState<IBatteryCatalogFilter>();
  const [openFilter, setOpenFilter] = useState(false);
  const [page, setPage] = useState(Number(searchParams.get('page')));
  const [searchInput, setSearchInput] = useState('');
  const [batteryCategories, setBatteryCategories] = useState<ICategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<{ id: number }>({ id: searchParams.get('category_id') ? Number(searchParams.get('category_id')) : batteryCategories[0]?.id });
  const user = JSON.parse(localStorage.getItem('userData')!);
  const [viewMode, setViewMode] = useState(user?.user_settings.products_view_mode || DEFAULT_VIEW);
  const [clearable, setClearable] = useState<boolean>(false);
  const ref = useRef<HTMLInputElement>(null);

  function getParams() {
    return {
      brand_ids: searchParams.getAll('brand_ids'),
      ah: searchParams.getAll('ah'),
      amp: searchParams.getAll('amp'),
      battery_id: searchParams.get('battery_id'),
      search: searchParams.get('search'),
      category_ids: searchParams.getAll('category_id')
    };
  }


  function getBatteryCategories(cancelToken) {
    setLoadingCategories(true);

    const filteredParams = filterObject(getParams());

    setLoading(true);
    http.catalogs.getCarBatteriesAllCategories(validateParams(filteredParams), cancelToken).then((res) => {

      setBatteryCategories(res);
      const firstValidCategory = res.find((category) => category.number_of_products !== 0);

      if (searchParams.get('category_id') || firstValidCategory) {
        const param = filterObject({
          ...filteredParams,
          category_ids: searchParams.get('category_id') ? searchParams.getAll('category_id') : [String(firstValidCategory?.id)]
        });

        getBatteriesData(validateParams(param));
      } else {

        setRefData(undefined);
        setLoading(false);
      }
    }).catch(() => {
      setRefData(undefined);
      setLoading(false);
    }).finally(() => setLoadingCategories(false));

    return cancelToken;
  }

  function getBatteriesData(data) {
    setLoading(true);

    const sortBySelectedOption = sortByOptions.filter((item) => String(item.value) == String(searchParams.get('sort')))[0] ?? [];

    const sortParams: SortByParams = {
      sort_by_column: String(searchParams.get('sort') ?? 'available'),
      sort_by_asc: String(searchParams.get('asc') ?? sortBySelectedOption?.default_sort_asc ?? 'false'),
    };

    const params = {
      ...data,
      ...sortParams,
      ...(isRetailerMecadepotChild() ? { per_page: 1000000 } : {}),
      page,
    };

    const validParams = validateParams(params);

    http.batteriesHttp
      .getBatteries(validParams)
      .then((res) => {
        setRefData(res.data);


        setTimeout(() => {
          if (params.page) {
            const firstChild = document.querySelector('.mecadepot-catalog-item');

            if (firstChild) {
              firstChild.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
          }
        }, 100);
      })
      .finally(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });

  }

  useEffect(() => {
    searchParams.set('sort', 'available');
    searchParams.set('asc', 'false');
    setSearchParams(searchParams);

  }, []);


  useEffect(() => {
    const cancelToken = axios.CancelToken.source();

    // const uniqueKeysCount = new Set(searchParams.keys()).size;

    // if ((searchParams.has('category_id') && [1, 2, 3, 4, 5, 6].includes(uniqueKeysCount))) {
    if (searchParams.has('category_id')) {
      const params = getParams();

      getBatteriesData(validateParams(filterObject(params)));
    } else {

      getBatteryCategories(cancelToken.token);
    }


    return () => {
      cancelToken.cancel('Anulare datorită schimbării de dependență.');
    };
  }, [i18n.language, search]);


  useEffect(() => {
    searchParams.delete('category_id');
    searchParams.delete('page');

    if (searchInput) {
      searchParams.set('search', searchInput);
    } else {
      searchParams.delete('search');
    }

    setSearchParams(searchParams);

  }, [searchInput]);

  useEffect(() => {

    const firstValidCategory = batteryCategories.find((category) => category.number_of_products !== 0);

    setActiveCategory({ id: Number(searchParams.get('category_id')) || Number(firstValidCategory?.id) });
  }, [batteryCategories, searchParams.get('category_id')]);


  function getBatteriesFilter(params) {
    const filterParams = {
      ...params,
      page,
    };

    const validFilterParams = validateParams(filterParams);

    http.batteriesHttp.getBatteryFilters(validFilterParams).then((res) => {
      setFilterData(res.data);
    });
  }

  const validateParams = (data) => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      acc[key] = value;

      return acc;
    }, {});
  };

  const handlePageClick = (pageN: number) => {
    setPage(pageN);
  };

  const onCategoryClick = (category) => {
    searchParams.set('category_id', category.id);
    searchParams.delete('page');
    setSearchParams(searchParams);
    setPage(1);

  };


  useEffect(() => {
    getBatteriesFilter({});
  }, [i18n.language]);

  useEffect(() => {
    if (searchParams.get('category_id')) {
      setActiveCategory({ id: Number(searchParams.get('category_id')) });
    }

  }, [batteryCategories, searchParams.get('category_id'), searchParams.get('sort'), searchParams.get('asc')]);

  function filterOptionsElement() {
    return (
      <div className="catalog-parts-filter">
        <button onClick={() => setOpenFilter(true)} className={`catalog-parts-button ${openFilter ? 'active' : ''}`}>
          {t('_filter_results_btn')}
        </button>
      </div>
    );
  }

  const customValidations: ICustomValidation[] = [
    {
      regex: new RegExp(/^.{3,}$/),
      message: t('_ref_search_min_length'),
    }
  ];

  const onClickReset = () => {
    ref.current!.value = '';
    setSearchInput('');
    setClearable(false);

  };

  const refInputChange = (event) => {
    const inputData = event.target.value.replace(/\s/g, '').length > 0 ? event.target.value : event.target.value.trim();

    setSearchInput(inputData);

    if (inputData.length) {
      setClearable(true);
    } else {
      setClearable(false);
    }
  };

  return (

    <div className="batteries-wrapper">
      <CatalogNavbar option={filterOptionsElement()} title="_battery_catalog" showSortBy={isRetailerMecadepotChild()} />
      <div className="separator"></div>

      <div className="filterOptions">
        <span className="search-parts-title">{t('_search')}</span>
        <TextInput
          name="search"
          placeholder="Ex: T170G"
          onChange={(e) => refInputChange(e)}
          withIcon
          customValidations={customValidations}
          clearable={clearable}
          onClickReset={onClickReset}
          ref={ref}
        />
        {(refData?.data?.length && refData?.data?.length > 0 && !isRetailerMecadepotChild()) ? <SortBy /> : null}
        <div className="results">
          {refData?.total_count} {t('_results')}
        </div>

        <ProductsViewMode viewMode={viewMode} setViewMode={setViewMode} />

      </div>
      <div className="retailer-catalog-batteries-container">
        <div className="catalog-batteries-sidebar">
          {batteryCategories && batteryCategories.length ? (
            <List
              content={batteryCategories?.map((item) => {
                return { ...item, id: item.id, name: item.name };
              })}
              images={batteryCategories?.map((item) => item.image)}
              itemClassName="parts-list"
              itemId="id"
              onItemClick={onCategoryClick}
              activeCategory={activeCategory}
            />
          ) : null}
        </div>


        <div className="wrapperItems">
          {loadingCategories || loading ? (
            <Spinner class="car-parts-spinner" />
          ) : refData?.data?.length ? (
            <ProductsViewModeChanger catalog={CATALOG_BATTERY} viewMode={viewMode} data={refData?.data} />
          ) : (
            <div className="no-parts-data">
              <div className="notFoundSvg">
                <PartsNotFoundImage />
              </div>
              <h1>{t('_batteries_not_found')}</h1>
            </div>
          )}
          {(refData && refData?.total_pages && refData?.page && refData?.total_pages > 1) && !loading ? (
            <Pagination pageCount={refData.total_pages} handlePageClick={handlePageClick} forcePage={refData.page} />
          ) : (
            ''
          )}
        </div>
      </div>
      <CatalogBatteryFilter getData={getBatteriesData} filterData={filterData} openFilter={openFilter} setOpenFilter={setOpenFilter} setPage={setPage} />
    </div>
  );
};

export default CatalogBatteries;
